import React, { useState, useEffect } from 'react';
import { useRealtorContext, Affiliate, CommissionRecord } from '../context/RealtorContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Section from '../components/layout/Section';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import DashboardCard from '../components/realtor/DashboardCard';
import CommissionChart from '../components/realtor/CommissionChart';
import AffiliateNetwork from '../components/realtor/AffiliateNetwork';
import AffiliateLinkModal from '../components/realtor/AffiliateLinkModal';
import toast from 'react-hot-toast';

const RealtorDashboardPage: React.FC = () => {
  const { 
    currentUser, 
    getMonthlyCommission,
    getAffiliatesByLevel,
    loading, 
    error
  } = useRealtorContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [commissionView, setCommissionView] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [monthlyData, setMonthlyData] = useState<{ month: string; amount: number }[]>([]);
  
  // Get current date
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
 /*  useEffect(() => {
    if (currentUser) {
      fetch(`https://uniquestatebackend.gomsolutions.com.ng/api/commissions/monthly/${currentUser.id}`)
        .then(res => res.json())
        .then(data => setMonthlyData(data));
    }
  }, [currentUser]); */
  useEffect(() => {
  if (currentUser) {
    const token = localStorage.getItem('token'); // Or get from context if you store it elsewhere
    fetch(`https://uniquestatebackend.gomsolutions.com.ng/api/commissions/monthly/${currentUser.id}`, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch commission data');
        return res.json();
      })
      .then(data => setMonthlyData(data))
      .catch(err => {
        setMonthlyData([]);
        console.error('Error fetching monthly commissions:', err);
      });
  }
}, [currentUser]);

  // Calculate monthly commissions for chart
  const getMonthlyCommissions = () => {
    if (!currentUser) return [];
    
    const months = [
      'January', 'February', 'March', 'April', 
      'May', 'June', 'July', 'August',
      'September', 'October', 'November', 'December'
    ];
    
    // Get last 6 months
    const lastSixMonths = [];
    for (let i = 5; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      const year = currentMonth - i < 0 ? currentYear - 1 : currentYear;
      
      lastSixMonths.push({
        month: months[monthIndex],
        amount: currentUser ? getMonthlyCommission(currentUser.id, monthIndex, year) : 0
      });
    }
    
    return lastSixMonths;
  };
  
  // Calculate statistics
  const getTotalProperties = () => {
    if (!currentUser) return 0;
    return currentUser.properties;
  };
  
  const getTotalPaidCommissions = () => {
    if (!currentUser) return 0;
    return currentUser.commissionHistory
      .filter((commission: CommissionRecord) => commission.status === 'paid')
      .reduce((total: number, commission: CommissionRecord) => total + commission.amount, 0);
  };

  const getPendingCommissions = () => {
    if (!currentUser) return 0;
    return currentUser.commissionHistory
      .filter((commission: CommissionRecord) => commission.status === 'pending')
      .reduce((total: number, commission: CommissionRecord) => total + commission.amount, 0);
  };

  const getTotalCancelledCommissions = () => {
    if (!currentUser) return 0;
    return currentUser.commissionHistory
      .filter((commission: CommissionRecord) => commission.status === 'cancelled')
      .reduce((total: number, commission: CommissionRecord) => total + commission.amount, 0);
  };
  
  // Get recent transactions
  const getRecentTransactions = () => {
    if (!currentUser) return [];
    
    return [...currentUser.commissionHistory]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 5);
  };

  // Static test data for commission chart
  const staticMonthlyData = [
    { month: 'January', amount: 120000 },
    { month: 'February', amount: 150000 },
    { month: 'March', amount: 110000 },
    { month: 'April', amount: 180000 },
    { month: 'May', amount: 170000 },
    { month: 'June', amount: 160000 },
  ];
  const staticQuarterlyData = [
    { month: 'Q1', amount: 380000 },
    { month: 'Q2', amount: 510000 },
    { month: 'Q3', amount: 420000 },
    { month: 'Q4', amount: 470000 },
  ];
  const staticYearlyData = [
    { month: '2022', amount: 1500000 },
    { month: '2023', amount: 1800000 },
    { month: '2024', amount: 2100000 },
  ];

  const getCommissionData = () => {
    if (commissionView === 'monthly') return staticMonthlyData;
    if (commissionView === 'quarterly') return staticQuarterlyData;
    if (commissionView === 'yearly') return staticYearlyData;
    return [];
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-grow pt-20">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-primary to-primary-dark text-white py-12">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h4 className="text-3xl md:text-4xl font-bold mb-2">
                  Welcome back, {currentUser?.name || 'Realtor'}!
                </h4>
                <p className="text-lg text-white/80">
                  Here's an overview of your performance and activities
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex space-x-3">
                <Button 
                  variant="secondary"
                  size="lg"
                  onClick={() => setIsModalOpen(true)}
                  style={{color: '#f5f8fa'}}
                  /* set the text color to `#fad826` on hover*/
                  onMouseOver={(e) => {
                    (e.target as HTMLButtonElement).style.color = '#fad826';
                  }}
                  onMouseOut={(e) => {
                    (e.target as HTMLButtonElement).style.color = '#f5f8fa';
                  }}
                >
                  Invite Affiliate
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => window.location.href = '/upload'}
                >
                  Upload Documents
                </Button>
                {/* The "Copy Affiliate Link" button is now handled by the modal */}
              </div>
            </div>
          </div>
        </div>
        
        <Section background="light">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <DashboardCard 
              title="Total Properties"
              value={getTotalProperties().toString()}
              icon="🏠"
              className="bg-blue-50"
              trend={{
                value: 12,
                isPositive: true
              }}
            />
            <DashboardCard
              title="Total Paid"
              value={`₦${getTotalPaidCommissions().toLocaleString()}`}
              icon="💰"
              className="bg-green-50"
              trend={{
                value: 8.5,
                isPositive: true
              }}
            />
            <DashboardCard
              title="Total Pending"
              value={`₦${getPendingCommissions().toLocaleString()}`}
              icon="⏳"
              className="bg-yellow-50"
              trend={{
                value: 3.2,
                isPositive: true
              }}
            />
            <DashboardCard
              title="Total Cancelled"
              value={`₦${getTotalCancelledCommissions().toLocaleString()}`}
              icon="❌"
              className="bg-red-50"
              trend={{
                value: -1.5,
                isPositive: false
              }}
            />
          </div>
          
          {/* Charts and Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Commission Chart */}
            <div className="lg:col-span-2">
              <Card className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                  <h2 className="text-xl font-bold text-primary mb-2 md:mb-0">
                    Commission History
                  </h2>
                  <div className="flex space-x-2">
                    <button
                      className={`px-3 py-1 rounded-md text-sm ${commissionView === 'monthly' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      onClick={() => setCommissionView('monthly')}
                    >
                      Monthly
                    </button>
                    <button
                      className={`px-3 py-1 rounded-md text-sm ${commissionView === 'quarterly' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      onClick={() => setCommissionView('quarterly')}
                    >
                      Quarterly
                    </button>
                    <button
                      className={`px-3 py-1 rounded-md text-sm ${commissionView === 'yearly' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      onClick={() => setCommissionView('yearly')}
                    >
                      Yearly
                    </button>
                  </div>
                </div>
                
                <CommissionChart data={getCommissionData()} />
                
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-primary/5 rounded-lg">
                    <p className="text-sm text-gray-600">Highest Month</p>
                    <p className="text-lg font-semibold text-primary">April</p>
                  </div>
                  <div className="text-center p-3 bg-primary/5 rounded-lg">
                    <p className="text-sm text-gray-600">Lowest Month</p>
                    <p className="text-lg font-semibold text-primary">January</p>
                  </div>
                  <div className="text-center p-3 bg-primary/5 rounded-lg">
                    <p className="text-sm text-gray-600">Average</p>
                    <p className="text-lg font-semibold text-primary">₦1.2M</p>
                  </div>
                  <div className="text-center p-3 bg-primary/5 rounded-lg">
                    <p className="text-sm text-gray-600">Growth</p>
                    <p className="text-lg font-semibold text-green-600">+18.5%</p>
                  </div>
                </div>
              </Card>
            </div>
            
            {/* Affiliate Network */}
            <div className="lg:col-span-1">
              <Card className="p-6">
                <h2 className="text-xl font-bold text-primary mb-6">
                  Your Affiliate Network
                </h2>
                
                {currentUser && (
                  <AffiliateNetwork 
                    realtor={{
                      name: currentUser.name,
                      image: currentUser.image
                    }}
                    affiliates={currentUser.affiliates.map((affiliate: Affiliate) => ({
                      level: affiliate.level,
                      name: affiliate.name,
                      image: "http://localhost:5000"+affiliate.image,
                      properties: affiliate.properties
                    }))}
                  />
                )}
                
                <div className="mt-6 space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Level 1 Affiliates</p>
                      <p className="text-sm text-gray-600">Direct referrals</p>
                    </div>
                    <div className="text-lg font-semibold text-primary">
                      {currentUser?.affiliates.filter((aff: Affiliate) => aff.level === 1).length || 0}
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Level 2 Affiliates</p>
                      <p className="text-sm text-gray-600">Indirect referrals</p>
                    </div>
                    <div className="text-lg font-semibold text-primary">
                      {currentUser?.affiliates.filter((aff: Affiliate) => aff.level === 2).length || 0}
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Affiliate Earnings</p>
                      <p className="text-sm text-gray-600">From network sales</p>
                    </div>
                    <div className="text-lg font-semibold text-green-600">
                      ₦{(currentUser?.affiliates.reduce((total: number, aff: Affiliate) => total + aff.commission, 0) || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
                
                <div className="mt-6">
                  <Button 
                    variant="primary"
                    className="w-full"
                    onClick={() => window.location.href = '/affiliate-management'}
                  >
                    Manage Affiliates
                  </Button>
                </div>
              </Card>
            </div>
          </div>
          
          {/* Recent Transactions */}
          <div className="mt-8">
            <Card className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                <h2 className="text-xl font-bold text-primary mb-2 md:mb-0">
                  Recent Transactions
                </h2>
                <Button 
                  variant="text"
                  onClick={() => window.location.href = '/commissions'}
                >
                  View All Transactions
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Property
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getRecentTransactions().map((transaction) => (
                      <tr key={transaction.id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{transaction.propertyTitle || 'N/A'}</div>
                          <div className="text-xs text-gray-500">ID: {transaction.propertyId || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{new Date(transaction.date).toLocaleDateString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">₦{transaction.amount.toLocaleString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            transaction.status === 'paid' 
                              ? 'bg-green-100 text-green-800' 
                              : transaction.status === 'pending' 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : 'bg-red-100 text-red-800'
                          }`}>
                            {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                    
                    {getRecentTransactions().length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                          No recent transactions found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
          
          {/* Quick Actions */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 border-l-4 border-primary">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Add New Property</h3>
              </div>
              <p className="text-gray-600 mb-4">
                List a new property for sale and start earning commissions.
              </p>
              <Button 
                variant="primary"
                className="w-full"
                onClick={() => window.location.href = '/add-property'}
              >
                Add Property
              </Button>
            </Card>
            
            <Card className="p-6 border-l-4 border-secondary">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center text-secondary mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Grow Your Network</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Invite new affiliates to join your network and increase your earnings.
              </p>
              <Button 
                variant="secondary"
                className="w-full"
                onClick={() => window.location.href = '/invite-affiliate'}
              >
                Invite Affiliates
              </Button>
            </Card>
            
            <Card className="p-6 border-l-4 border-green-500 border-primary-green">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center text-green-500 mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Record Commission</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Record a new commission for a property sale or referral.
              </p>
              <Button 
                variant="success"
                className="w-full"
                onClick={() => window.location.href = '/commissions'}
              >
                Record Commission
              </Button>
            </Card>
          </div>
        </Section>
      </main>
      
      <Footer />

      {currentUser && (
        <AffiliateLinkModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          affiliateLink={currentUser.affiliateLink || ''}
        />
      )}
    </div>
  );
};

export default RealtorDashboardPage;
